/**
 * Natural-language → guided formula interpretation.
 *
 * Users describe what they want ("average deal size", "days between created
 * date and close date", "if amount > 50000 then 1 else 0") and we map that to
 * the same structured formula the guided builder produces. Fully local and
 * deterministic — no model call — so it stays instant and testable.
 */

import i18n from "@/lib/i18n";
import {
  NUMERIC_TYPES,
  TODAY_TOKEN,
  pickFormulaFields,
  type FormulaFieldOption,
  type FormulaKind,
  type FormulaSlots,
  type IfFormula,
} from "@/lib/utils/formula-fields";

export interface InterpretedFormula {
  kind: FormulaKind;
  slots: FormulaSlots;
  ifState?: IfFormula;
  /** Human explanation of what was understood. */
  summary: string;
  /** Things we could not resolve — shown so the user can finish manually. */
  gaps: string[];
}

const DATE_TYPES = ["date", "datetime"];

function norm(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

/** Word-level match that tolerates inflection: "create" ↔ "created", "day" ↔ "days". */
function wordHit(word: string, textWords: string[]): boolean {
  return textWords.some(w =>
    w === word || (w.length >= 4 && word.startsWith(w)) || (word.length >= 4 && w.startsWith(word)),
  );
}

/**
 * Words that most of the candidate labels share, so they can't identify one.
 *
 * Among a pool of date fields — Close Date, Created Date, Last Activity Date —
 * `date` is in every label and says nothing about which one the user meant. This
 * is the whole tie-breaker: "the create date" hit `date` in both Close Date and
 * Created Date, the two scored equally, and array order picked the wrong one,
 * which is how "days between the create date and the close date" came out as
 * DATEDIF(close, created) and produced a NEGATIVE day count.
 */
function genericWords(pool: FormulaFieldOption[]): Set<string> {
  if (pool.length < 2) return new Set();
  const counts = new Map<string, number>();
  for (const f of pool) {
    for (const w of new Set(labelWords(f.label))) {
      counts.set(w, (counts.get(w) ?? 0) + 1);
    }
  }
  const shared = new Set<string>();
  for (const [word, n] of counts) {
    if (n > pool.length / 2) shared.add(word);
  }
  return shared;
}

function labelWords(label: string): string[] {
  return norm(label).split(" ").filter(w => w.length > 2);
}

/**
 * How well a field label matches the text. 0 means no match.
 *
 * A match has to rest on at least one word that isn't generic across the pool
 * (see `genericWords`) — otherwise "signature date" would "match" Close Date on
 * the shared word alone. Labels made ENTIRELY of shared words (a field simply
 * called "Date") keep the generic hit, since it's all they have. Beyond that,
 * score on the FRACTION of the label matched, so a full 2-of-2 beats a 1-of-2;
 * matched characters and then label length break what's left.
 */
function scoreField(text: string, f: FormulaFieldOption, generic: Set<string>): number {
  const t = norm(text);
  const label = norm(f.label);
  if (!label || !t) return 0;
  if (t.includes(label)) return 1000 + label.length;
  const textWords = t.split(" ").filter(Boolean);
  const words = labelWords(label);
  if (!words.length) return 0;
  const hits = words.filter(w => wordHit(w, textWords));
  if (!hits.length) return 0;
  const distinctive = words.filter(w => !generic.has(w));
  if (distinctive.length && !hits.some(w => !generic.has(w))) return 0;
  const coverage = hits.length / words.length;
  const hitChars = hits.reduce((n, w) => n + w.length, 0);
  return Math.round(coverage * 100) * 10 + hitChars + label.length / 100;
}

/** Score how well a field label appears in the text. */
function matchField(
  text: string,
  fields: FormulaFieldOption[],
  types?: string[],
): FormulaFieldOption | null {
  const pool = types ? pickFormulaFields(fields, types) : fields;
  const generic = genericWords(pool);
  let best: { f: FormulaFieldOption; score: number } | null = null;
  for (const f of pool) {
    const score = scoreField(text, f, generic);
    if (score > 0 && (!best || score > best.score)) best = { f, score };
  }
  return best ? best.f : null;
}

/**
 * Fill two ordered slots from two text fragments at once.
 *
 * Picking greedily (best match for the first fragment, then best of what's left
 * for the second) hands the shared-word tie to whichever slot is resolved first.
 * Scoring both assignments and keeping the better total is what makes the slot
 * ORDER follow the sentence — which for DATEDIF is the difference between a
 * positive and a negative result.
 */
function matchFieldPair(
  aText: string,
  bText: string,
  fields: FormulaFieldOption[],
  types?: string[],
): [FormulaFieldOption | null, FormulaFieldOption | null] {
  const pool = types ? pickFormulaFields(fields, types) : fields;
  const generic = genericWords(pool);
  let best: { a: FormulaFieldOption; b: FormulaFieldOption; score: number } | null = null;
  for (const a of pool) {
    for (const b of pool) {
      if (a.key === b.key) continue;
      const scoreA = scoreField(aText, a, generic);
      const scoreB = scoreField(bText, b, generic);
      // BOTH sides must match on their own fragment. Scoring the sum alone let a
      // strong match on one side carry an unrelated field into the other slot,
      // which is worse than reporting the slot as unfilled.
      if (!scoreA || !scoreB) continue;
      const score = scoreA + scoreB;
      if (!best || score > best.score) best = { a, b, score };
    }
  }
  if (best) return [best.a, best.b];
  // Only one side could be matched (or the pool has a single field): fall back to
  // independent matches, leaving the unmatched slot empty for the caller to
  // report as a gap.
  const a = matchField(aText, pool);
  return [a, matchField(bText, a ? pool.filter(f => f.key !== a.key) : pool)];
}

const OP_WORDS: { re: RegExp; op: string }[] = [
  { re: /\b(greater than or equal to|at least|no less than|>=)\b/i, op: ">=" },
  { re: /\b(less than or equal to|at most|no more than|<=)\b/i, op: "<=" },
  { re: /\b(greater than|more than|over|above|exceeds|>)\b/i, op: ">" },
  { re: /\b(less than|under|below|fewer than|<)\b/i, op: "<" },
  { re: /\b(is not|isn't|not equal to|does not equal|<>|!=)\b/i, op: "<>" },
  { re: /\b(equals|equal to|is exactly|is|=)\b/i, op: "=" },
];

/**
 * Concepts the formula language genuinely cannot express, and what to say instead.
 *
 * These are limits of the grammar, not of the matcher: no arithmetic operators
 * exist (SUM is the only way to add), and there is no clock — every input is a
 * field on the record. Naming the specific blocker beats a generic "couldn't
 * understand that", which left users re-wording a request that was never going
 * to work.
 */
const UNSUPPORTED_CONCEPTS: { re: RegExp; message: string }[] = [
  {
    // Narrowed when TODAY() shipped: "days since X" is now expressible. What remains
    // unsupported is CALENDAR BUCKETING ("this month", "this quarter") — a formula
    // returns one value per record, it cannot group records into periods.
    re: /\b(this month|this year|this quarter|this week|last month|last year|last quarter|year to date|month to date|per month|per quarter|by month|by quarter)\b/,
    message:
      "Formulas can't bucket records into calendar periods — they compute one value per record. For \"days since\" or \"days until\", use a date field and Today.",
  },
  {
    // The clock is a whole DATE. Time-of-day arithmetic has no representation: DATEDIF's
    // only unit is whole days and there is no NOW().
    re: /\b(current time|time of day|right now)\b/,
    message:
      "Formulas work in whole days, not times — there's no \"now\". Use Today with a date field for a day count.",
  },
  {
    // DATEDIF's only unit is "d". Any other unit needs division, which the grammar
    // doesn't have — so this is a real gap, not a wording problem.
    re: /\b(months?|years?|weeks?|hours?|minutes?)\s+(since|until|till|between|old|ago)\b/,
    message:
      "Formulas count whole days only — there are no months, weeks or hours (that would need division). Ask for a number of days instead.",
  },
  {
    re: /\b(times|multiplied|multiply|product of|divided|divide|per|ratio|percent of|percentage of|minus|subtract|difference of)\b/,
    message:
      "There's no multiplication, division or subtraction in formulas. Adding is supported (SUM), as are AVERAGE, COUNT, DATEDIF, IF/THEN and LOOKUP.",
  },
  {
    re: /\b(concat|join|combine text|append|prefix|suffix|first name and last name)\b/,
    message:
      "Formulas can't build text out of other fields. They return a number, a date, or one of the values you give an IF/THEN.",
  },
];

/**
 * A message naming the unsupported concept in `input`, or null. Used to explain a
 * generation failure rather than only suggesting the user re-word it.
 */
export function unsupportedConceptIn(input: string): string | null {
  const t = norm(input);
  if (!t) return null;
  return UNSUPPORTED_CONCEPTS.find(c => c.re.test(t))?.message ?? null;
}

function detectKind(t: string): FormulaKind | null {
  if (/\b(if|when|whenever)\b/.test(t) && /\b(then|otherwise|else)\b/.test(t)) return "if";
  if (/\b(average|avg|mean)\b/.test(t)) return "average";
  if (/\b(days between|days since|days until|days till|number of days|date difference|difference between .* dates?|elapsed|age in days|how long)\b/.test(t)) return "datedif";
  if (/\b(count|how many|number of)\b/.test(t)) return "count";
  if (/\b(look ?up|pull from|from the related|from the associated|inherit)\b/.test(t)) return "lookup";
  if (/\b(sum|total|add up|combined)\b/.test(t)) return "sum";
  if (/\b(if|when)\b/.test(t)) return "if";
  return null;
}

export function interpretFormula(
  input: string,
  fields: FormulaFieldOption[],
): InterpretedFormula | null {
  const raw = (input || "").trim();
  if (!raw) return null;
  const t = raw.toLowerCase();
  const kind = detectKind(t);
  if (!kind) return null;

  const gaps: string[] = [];

  if (kind === "if") {
    // Split "if <cond> then <a> else <b>"
    const condSrc = (t.match(/\b(?:if|when|whenever)\b([\s\S]*?)(?:\bthen\b|,|$)/i)?.[1] ?? t).trim();
    const thenSrc = t.match(/\bthen\b([\s\S]*?)(?:\b(?:else|otherwise)\b|$)/i)?.[1]?.trim() ?? "";
    const elseSrc = t.match(/\b(?:else|otherwise)\b([\s\S]*)$/i)?.[1]?.trim() ?? "";

    // Support one AND/OR join across simple clauses.
    const join: "AND" | "OR" = /\bor\b/.test(condSrc) && !/\band\b/.test(condSrc) ? "OR" : "AND";
    const clauses = condSrc.split(/\b(?:and|or)\b/i).map(s => s.trim()).filter(Boolean);

    const conds = clauses.map(clause => {
      const opHit = OP_WORDS.find(o => o.re.test(clause));
      const op = opHit?.op ?? "=";
      const [lhs, rhs] = opHit
        ? clause.split(opHit.re).filter((_, i, arr) => i === 0 || i === arr.length - 1)
        : [clause, ""];
      const f = matchField(lhs ?? clause, fields);
      const value = (rhs ?? "").replace(/[",.]$/g, "").replace(/[$,]/g, "").trim();
      if (!f) gaps.push(`Could not match a field in "${clause.trim()}"`);
      if (!value) gaps.push(`Missing a comparison value in "${clause.trim()}"`);
      return { field: f?.key ?? "", op, value };
    }).filter(c => c.field || c.value);

    const clean = (s: string) => s.replace(/[".,]+$/g, "").replace(/^["']|["']$/g, "").trim();
    const then = clean(thenSrc);
    const els = clean(elseSrc);
    if (!then) gaps.push("Missing the Then value");
    if (!els) gaps.push("Missing the Else value");

    return {
      kind: "if",
      slots: {},
      ifState: {
        conds: conds.length ? conds : [{ field: "", op: "=", value: "" }],
        join,
        then,
        else: els,
      },
      // The displayed number floors at 1 while the plural form follows the real
      // count, so an empty builder still reads exactly as it did before.
      summary: `Conditional (IF/THEN) with ${conds.length || 1} ${i18n.t("plurals.condition", { count: conds.length })} joined by ${join}.`,
      gaps,
    };
  }

  if (kind === "datedif") {
    // "days since X" / "days until X" name ONE field; the other side is the clock.
    // Order matters — DATEDIF(start, end) is end minus start — so "since" puts the
    // field first and "until" puts it second, which is what keeps the count positive.
    const sinceUntil = t.match(
      /\b(since|until|till|to)\b\s+(?:the\s+)?([\s\S]+?)\s*$/i);
    if (sinceUntil && /\b(days?|age|how long)\b/.test(t) && !/\bbetween\b/.test(t)) {
      // "days until today" names the clock on BOTH sides — always zero, and never what
      // anyone means. There is no formula to offer, so say nothing rather than hand back
      // a DATEDIF with an empty slot.
      if (/^(today|now|current date|the present|todays? date)$/i.test(sinceUntil[2].trim())) {
        return null;
      }
      const field = matchField(sinceUntil[2], fields, DATE_TYPES);
      if (field) {
        const since = /^(since|from)$/i.test(sinceUntil[1]);
        return {
          kind,
          slots: since
            ? { a: field.key, b: TODAY_TOKEN }
            : { a: TODAY_TOKEN, b: field.key },
          summary: since
            ? `Whole days from ${field.label} until today (DATEDIF).`
            : `Whole days from today until ${field.label} (DATEDIF).`,
          gaps,
        };
      }
      gaps.push(`Could not match a date field in "${sinceUntil[2].trim()}"`);
    }
    // Try "between X and Y" first, then fall back to two distinct date fields.
    // The slots are ORDERED — DATEDIF(start, end) is end minus start — so both
    // are resolved together against their own fragment of the sentence.
    const between = t.match(/between([\s\S]*?)\band\b([\s\S]*)$/i);
    const aSrc = between?.[1] ?? t;
    const bSrc = between?.[2] ?? t;
    const [a, b] = matchFieldPair(aSrc, bSrc, fields, DATE_TYPES);
    if (!a) gaps.push("Could not identify the start date field");
    if (!b) gaps.push("Could not identify the end date field");
    return {
      kind,
      slots: { a: a?.key ?? "", b: b?.key ?? "" },
      summary: "Whole days between two date fields (DATEDIF).",
      gaps,
    };
  }

  if (kind === "lookup") {
    // A LOOKUP names a *relation* ("company", "contact") and a field on the far
    // side of it — neither of which this local matcher has any vocabulary for,
    // since `fields` only describes the current object. Hand the user an empty
    // LOOKUP to fill in rather than guessing a same-object field.
    return {
      kind,
      slots: { a: "", b: "" },
      summary: "Pulls a value from a related record (LOOKUP).",
      gaps: ["Choose the related record and the field to pull from it"],
    };
  }

  const numeric = kind === "sum" || kind === "average";
  const f = matchField(t, fields, numeric ? NUMERIC_TYPES : undefined);
  if (!f) gaps.push(numeric ? "Could not identify a numeric field" : "Could not identify a field");
  return {
    kind,
    slots: { a: f?.key ?? "" },
    summary:
      kind === "sum" ? "Adds up a numeric field (SUM)."
      : kind === "average" ? "Averages a numeric field (AVERAGE)."
      : "Counts the values of a field (COUNT).",
    gaps,
  };
}
